/**
 * BodyRegionAnnotation - Independent panel for body region annotations
 *
 * Wraps drawing tools (rectangle, circle, line) and predefined region selector
 * in an AnnotationPanel for independent save functionality.
 *
 * This is Type 1 of 3 annotation types:
 * - Body Region: Highlight regions of interest on the body
 * - Distance Goal: Distance constraints between landmarks
 * - Movement Goal: Track landmark movement during phases
 */

import { useState, useCallback, useEffect } from 'react';
import {
  PaintBrushIcon,
  SquaresPlusIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui';
import AnnotationPanel from './AnnotationPanel';
import PredefinedRegionSelector from './PredefinedRegionSelector';
import type { Annotation } from './RegionAnnotator';
import type { PredefinedRegion } from '@/services/annotationApi';

interface BodyRegionAnnotationProps {
  // Panel state
  isExpanded: boolean;
  onToggle: () => void;
  panelNumber?: number;
  totalPanels?: number;

  // Region data
  pendingAnnotation: Annotation | null;
  onPendingAnnotationChange: (annotation: Annotation | null) => void;

  // Drawing mode control (controlled by parent RegionAnnotator)
  // Note: Line removed - use Axis Alignment panel for line/axis annotations
  drawingMode: 'rectangle' | 'circle' | null;
  onDrawingModeChange: (mode: 'rectangle' | 'circle' | null) => void;

  // Predefined region callback
  onSelectPredefinedRegion: (region: PredefinedRegion) => void;
  templateHandedness?: 'right' | 'left' | 'ambidextrous';
  videoId?: string; // Video ID passed through to PredefinedRegionSelector for functional region resolution

  // Tracking options
  tracking: boolean;
  onTrackingChange: (tracking: boolean) => void;

  // Save callback
  onSave: (annotation: Annotation, label: string, notes: string, importance: number) => Promise<void>;

  // Status
  landmarkDetectionStatus?: string | null;
  isSaved?: boolean;

  className?: string;
}

export default function BodyRegionAnnotation({
  isExpanded,
  onToggle,
  panelNumber = 1,
  totalPanels = 3,
  pendingAnnotation,
  onPendingAnnotationChange,
  drawingMode,
  onDrawingModeChange,
  onSelectPredefinedRegion,
  templateHandedness = 'right',
  videoId,
  tracking,
  onTrackingChange,
  onSave,
  landmarkDetectionStatus,
  isSaved = false,
  className,
}: BodyRegionAnnotationProps) {
  // Local form state
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [importance, setImportance] = useState(50);
  const [isSaving, setIsSaving] = useState(false);
  const [showRegionSelector, setShowRegionSelector] = useState(false);
  const [localIsSaved, setLocalIsSaved] = useState(false);

  // Hydrate form fields when pendingAnnotation changes (e.g., from edit button).
  // Without this, editing loads the annotation but leaves form fields at defaults.
  useEffect(() => {
    if (pendingAnnotation) {
      if (pendingAnnotation.description) setLabel(pendingAnnotation.description);
      if (pendingAnnotation.notes) setNotes(pendingAnnotation.notes);
      if (pendingAnnotation.importance) {
        const impMap: Record<string, number> = { low: 15, medium: 50, high: 75, critical: 95 };
        setImportance(impMap[pendingAnnotation.importance] ?? 50);
      }
      setLocalIsSaved(false);
    }
  }, [pendingAnnotation?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle predefined region selection
  const handleSelectPredefinedRegion = useCallback((region: PredefinedRegion) => {
    setShowRegionSelector(false);
    setLabel(region.name);
    setNotes(region.description || '');

    // Map importance string to number
    const importanceMap: Record<string, number> = {
      low: 15,
      medium: 50,
      high: 75,
      critical: 95,
    };
    setImportance(importanceMap[region.default_importance || 'medium'] || 50);

    // Delegate to parent handler
    onSelectPredefinedRegion(region);
  }, [onSelectPredefinedRegion]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!pendingAnnotation || !label.trim()) return;

    setIsSaving(true);
    try {
      await onSave(pendingAnnotation, label.trim(), notes.trim(), importance);

      // Clear form after successful save
      setLabel('');
      setNotes('');
      setImportance(50);
      setLocalIsSaved(true);

      // Reset saved indicator after delay
      setTimeout(() => setLocalIsSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save body region annotation:', error);
    } finally {
      setIsSaving(false);
    }
  }, [pendingAnnotation, label, notes, importance, onSave]);

  // Cancel drawing mode
  const handleCancelDrawing = useCallback(() => {
    onDrawingModeChange(null);
    onPendingAnnotationChange(null);
  }, [onDrawingModeChange, onPendingAnnotationChange]);

  // Can save when we have a pending annotation and a label
  const canSave = !!pendingAnnotation && !!label.trim();

  return (
    <>
      <AnnotationPanel
        title="Body Region"
        subtitle="Highlight areas of interest"
        icon={<PaintBrushIcon className="w-5 h-5" />}
        accentColor="blue"
        panelNumber={panelNumber}
        totalPanels={totalPanels}
        isExpanded={isExpanded}
        onToggle={onToggle}
        label={label}
        onLabelChange={setLabel}
        notes={notes}
        onNotesChange={setNotes}
        importance={importance}
        onImportanceChange={setImportance}
        canSave={canSave}
        isSaving={isSaving}
        isSaved={localIsSaved || isSaved}
        onSave={handleSave}
        saveButtonText="Save Body Region"
        className={className}
      >
        {/* Quick Select Predefined Region */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowRegionSelector(true)}
            className={cn(
              'w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg',
              'bg-gradient-to-r from-blue-500 to-blue-600 text-white',
              'font-semibold text-sm',
              'shadow-md hover:shadow-lg',
              'transition-all duration-200',
              'hover:from-blue-600 hover:to-blue-700',
              'active:scale-[0.98]'
            )}
          >
            <SquaresPlusIcon className="w-5 h-5" />
            <span>Quick Select Body Region</span>
          </button>
          <p className="text-xs text-text-secondary mt-2 text-center">
            Choose from predefined anatomical regions with pre-mapped landmarks
          </p>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-border-subtle" />
          <span className="text-xs font-medium text-text-muted uppercase tracking-wider">
            Or draw manually
          </span>
          <div className="flex-1 h-px bg-border-subtle" />
        </div>

        {/* Drawing Tools */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={drawingMode === 'rectangle' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => onDrawingModeChange('rectangle')}
            className="flex-1 min-w-[80px]"
          >
            Rectangle
          </Button>
          <Button
            variant={drawingMode === 'circle' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => onDrawingModeChange('circle')}
            className="flex-1 min-w-[80px]"
          >
            Circle
          </Button>
          {drawingMode && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancelDrawing}
            >
              Cancel
            </Button>
          )}
        </div>

        {/* Drawing Instructions */}
        {drawingMode && (
          <div className="mt-3 p-3 bg-info/10 border border-info/30 rounded-lg text-sm text-info">
            {drawingMode === 'rectangle' && 'Click and drag on the video to draw a rectangle'}
            {drawingMode === 'circle' && 'Click center, then drag to set radius'}
          </div>
        )}

        {/* Tracking Toggle */}
        <div className="mt-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={tracking}
              onChange={(e) => onTrackingChange(e.target.checked)}
              className="mt-0.5 w-4 h-4 text-accent border-border-strong rounded focus:ring-accent"
            />
            <div>
              <span className="text-sm font-medium text-text-primary">
                Track through video (pose-based)
              </span>
              {tracking && (
                <p className="text-xs text-text-secondary mt-1">
                  The system will detect body landmarks inside your region and track them throughout the video.
                </p>
              )}
            </div>
          </label>
        </div>

        {/* Pending Annotation Status */}
        {pendingAnnotation && (
          <div className="mt-4 p-3 bg-success/10 border border-success/30 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-success">
              <svg className="h-5 w-5 text-success" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">
                Region drawn - {pendingAnnotation.type}
              </span>
            </div>
            {pendingAnnotation.landmark_indices && pendingAnnotation.landmark_indices.length > 0 && (
              <p className="text-xs text-success/70 mt-1 ml-7">
                {pendingAnnotation.landmark_indices.length} landmark{pendingAnnotation.landmark_indices.length !== 1 ? 's' : ''} detected
              </p>
            )}
          </div>
        )}

        {/* Landmark Detection Status */}
        {landmarkDetectionStatus && (
          <div
            className={cn(
              'mt-3 p-2 rounded-lg border text-sm',
              landmarkDetectionStatus.includes('✓')
                ? 'bg-success/10 border-success/30 text-success'
                : landmarkDetectionStatus.includes('⚠')
                ? 'bg-warning/10 border-warning/30 text-warning'
                : 'bg-info/10 border-info/30 text-info'
            )}
          >
            {landmarkDetectionStatus}
          </div>
        )}

        {/* No pending annotation message */}
        {!pendingAnnotation && !drawingMode && (
          <div className="mt-4 p-3 bg-dark-elevated-1 border border-border-subtle rounded-lg text-center">
            <p className="text-sm text-text-secondary">
              Select a predefined region or draw on the video above
            </p>
          </div>
        )}
      </AnnotationPanel>

      {/* Predefined Region Selector Modal */}
      {showRegionSelector && (
        <PredefinedRegionSelector
          onSelect={handleSelectPredefinedRegion}
          onClose={() => setShowRegionSelector(false)}
          templateHandedness={templateHandedness}
          videoId={videoId}
        />
      )}
    </>
  );
}
